import Tooltip from "@corvu/tooltip";
import { Button as RawButton } from "@kobalte/core/button";
import { createQuery } from "@tanstack/solid-query";
import { For, Match, Show, Switch, createMemo, createSignal } from "solid-js";
import { Temporal } from "temporal-polyfill";
import { Divider } from "~/components/ui/divider";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "~/components/ui/drawer";
import {
  getPayoutControllerGetPayoutHistoryApiV1Url,
  payoutControllerGetPayoutHistoryApiV1,
} from "~/lib/api/__generated__/api.schema";
import { type PayoutHistoryItem, PayoutHistoryItemStatus } from "~/lib/api/__generated__/models";
import { environment } from "~/lib/environment";
import { latest } from "~/lib/latest";
import { cn } from "~/lib/utils";

const typeFilters = [
  {
    label: "All",
    value: undefined,
  },
  {
    label: "Payout Completed",
    value: "COMPLETED",
  },
  {
    label: "Payout Failed",
    value: "FAILED",
  },
] as const satisfies {
  label: string;
  value: undefined | "COMPLETED" | "FAILED";
}[];

type HistoryTypeFilter = (typeof typeFilters)[number];

export const PayoutHistory = () => {
  const [filterDrawerOpen, setFilterDrawerOpen] = createSignal(false);
  const [typeFilter, setTypeFilter] = createSignal<HistoryTypeFilter>(typeFilters[0]);
  const historyQuery = createQuery(() => ({
    queryKey: [getPayoutControllerGetPayoutHistoryApiV1Url()],
    queryFn: () => payoutControllerGetPayoutHistoryApiV1(),
  }));

  const payoutHistory = createMemo(() => {
    return latest(() => historyQuery.data?.data.items) ?? [];
  });

  return (
    <div class="flex flex-col">
      <div class="flex flex-col mb-16px env-desktop:(mt-23px) env-mobile:(mt-40px)">
        <div class="flex items-center justify-between">
          <h3 class="prose-unbounded-lg env-desktop:(prose-unbounded-xl)">History</h3>
          <RawButton
            class="flex items-center gap-6px text-primary text-sm-b px-16px env-desktop:(py-9px rounded-full bg-background-offWhite leading-1)"
            onClick={() => setFilterDrawerOpen(true)}
          >
            {typeFilter().label}
            <i class="inline-block i-bp-dropdown" />
          </RawButton>

          <Drawer open={filterDrawerOpen()} onOpenChange={setFilterDrawerOpen}>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>Filter by</DrawerTitle>
              </DrawerHeader>
              <ul class="flex flex-col py-10px">
                <For each={typeFilters}>
                  {(f) => (
                    <li>
                      <RawButton
                        class="flex w-full items-center justify-between px-20px h-58px"
                        onClick={() => {
                          setTypeFilter(f);
                          setFilterDrawerOpen(false);
                        }}
                      >
                        {f.label}
                        <Show when={typeFilter().value === f.value}>
                          <i class="inline-block i-bp-check-full text-primary" />
                        </Show>
                      </RawButton>
                    </li>
                  )}
                </For>
              </ul>
            </DrawerContent>
          </Drawer>
        </div>
      </div>

      <Show
        when={payoutHistory().length > 0}
        fallback={
          <>
            <div class="text-tertiary flex flex-col items-center justify-center gap-2 flex-1">
              <i class="block i-bp-invoice" />
              <p class="prose-sm text-center">
                You don’t have any payouts yet
                <br />
                Payouts will appear here
              </p>
            </div>
            {/* TODO: make this bank add button */}
            <div class="my-4" />
          </>
        }
      >
        <Show when={environment === "mobile"}>
          <ul class="flex flex-col gap-32px">
            <For each={payoutHistory().filter((h) => (typeFilter().value ? h.status === typeFilter().value : true))}>
              {(item) => (
                <li>
                  <div>
                    <span class="prose-xs text-secondary">
                      {Temporal.Instant.from(item.startAt)
                        .toZonedDateTimeISO(Temporal.Now.timeZoneId())
                        .toLocaleString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                    </span>
                    <Divider size="full" class="h-[0.75px]! mt-12px mb-16px" />
                    <div class="flex justify-between">
                      <span class="prose-md-b text-primary">${item.amount.toLocaleString()}</span>
                      <PayoutStatusTooltip item={item} />
                    </div>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Show>
        <Show when={environment === "desktop"}>
          <table class="w-full table-fixed">
            <thead class="h-48px">
              <tr>
                <th colspan={3}>
                  <Divider size="full" class="h-[0.75px]!" />
                </th>
              </tr>
              <tr>
                <th class="max-w-[300px] px-12px text-left text-xs text-primary">Date</th>
                <th class="max-w-[220px] px-12px text-left text-xs text-primary">Status</th>
                <th class="max-w-full px-12px text-right text-xs text-primary">Payout Total</th>
              </tr>
            </thead>
            <tbody>
              <For each={payoutHistory().filter((h) => (typeFilter().value ? h.status === typeFilter().value : true))}>
                {(item) => (
                  <>
                    <tr>
                      <td colspan="3">
                        <Divider size="full" />
                      </td>
                    </tr>
                    <tr class="h-48px">
                      <td class="px-12px text-sm text-primary">
                        {Temporal.Instant.from(item.startAt)
                          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
                          .toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                      </td>
                      <td class="px-12px">
                        <PayoutStatusTooltip item={item} />
                      </td>
                      <td class="text-right px-12px">
                        <span class="prose-sm-b text-primary">${item.amount.toLocaleString()}</span>
                      </td>
                    </tr>
                  </>
                )}
              </For>
              <tr>
                <td colspan="3">
                  <Divider size="full" class="h-[0.75px]!" />
                </td>
              </tr>
            </tbody>
          </table>
        </Show>
      </Show>
    </div>
  );
};

const PayoutStatusTooltip = (props: { item: PayoutHistoryItem }) => {
  return (
    <Tooltip
      placement="top"
      openDelay={100}
      closeOnPointerDown={false}
      openOnHover={environment === "desktop"}
      floatingOptions={{
        offset: 4,
        shift: true,
        size: {
          fitViewPort: true,
        },
      }}
      open={props.item.status === PayoutHistoryItemStatus.COMPLETED ? false : undefined}
    >
      <Tooltip.Anchor class="flex items-center w-min">
        <Tooltip.Trigger>
          <div
            class={cn(
              "flex gap-4px items-center px-6px py-3px mr-16px rounded-full w-min env-mobile:mr-4px",
              props.item.status === PayoutHistoryItemStatus.PENDING
                ? "bg-functional-orange-light"
                : props.item.status === PayoutHistoryItemStatus.FAILED
                  ? "bg-functional-red-light"
                  : "bg-functional-green-light",
            )}
          >
            <Switch>
              <Match when={props.item.status === PayoutHistoryItemStatus.PENDING}>
                <i class="i-bp-pending text-functional-orange" />
                <span class="prose-xs text-functional-orange">Pending</span>
              </Match>
              <Match when={props.item.status === PayoutHistoryItemStatus.FAILED}>
                <i class="i-bp-failed text-functional-red-dark" />
                <span class="prose-xs text-functional-red-dark">Failed</span>
              </Match>
              <Match when={props.item.status === PayoutHistoryItemStatus.COMPLETED}>
                <i class="i-bp-checked text-functional-green-dark" />
                <span class="prose-xs text-functional-green-dark">Paid</span>
              </Match>
            </Switch>
          </div>
        </Tooltip.Trigger>
      </Tooltip.Anchor>
      <Tooltip.Portal>
        <Tooltip.Content class="relative rounded-8px bg-[#2F2F2F] z-999 w-280px shadow-[0px_4px_12px_0px_rgba(0,0,0,0.15)] overflow-y-auto env-mobile:(w-180px)">
          <div class="px-12px py-8px text-white prose-xs leading-[1.4] text-center">
            <Switch>
              <Match when={props.item.status === PayoutHistoryItemStatus.PENDING}>
                Withdrawal in progress. Payout will be deposited within the first week of the following month.
              </Match>
              <Match when={props.item.status === PayoutHistoryItemStatus.FAILED}>
                Check your payment details or contact support for assistance.
              </Match>
            </Switch>
          </div>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip>
  );
};
